import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Save, Plus, Trash2, Award } from 'lucide-react';
import api from '../../utils/api';

/**
 * AcademyQuizAdmin — Admin creates/edits quizzes per course.
 * Backend: POST /academy/admin/course/{course_id}/quiz  +  GET /academy/course/{id}/quiz
 * Passing the quiz unlocks the certificate PDF download.
 */
const AcademyQuizAdmin = () => {
  const [courses, setCourses] = useState([]);
  const [courseId, setCourseId] = useState('');
  const [passing, setPassing] = useState(60);
  const [questions, setQuestions] = useState([]);

  useEffect(() => {
    api.get('/academy/courses').then((r) => setCourses(r.data.courses || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!courseId) return setQuestions([]);
    api.get(`/academy/course/${courseId}/quiz`).then((r) => {
      if (r.data.exists) {
        setPassing(r.data.passing_score || 60);
        // Backend hides correct_index/explanation on GET — start blank so admin re-enters
        setQuestions((r.data.questions || []).map((q) => ({ question: q.question, options: q.options, correct_index: 0, explanation: '' })));
      } else {
        setQuestions([]);
      }
    }).catch(() => setQuestions([]));
  }, [courseId]);

  const addQ = () => setQuestions([...questions, { question: '', options: ['', '', ''], correct_index: 0, explanation: '' }]);
  const removeQ = (i) => setQuestions(questions.filter((_, idx) => idx !== i));
  const updateQ = (i, patch) => setQuestions(questions.map((q, idx) => (idx === i ? { ...q, ...patch } : q)));
  const updateOpt = (i, j, val) => updateQ(i, { options: questions[i].options.map((o, k) => (k === j ? val : o)) });
  const addOpt = (i) => updateQ(i, { options: [...questions[i].options, ''] });

  const save = async () => {
    if (!courseId) return toast.error('Select a course');
    const clean = questions
      .filter((q) => q.question.trim() && q.options.filter((o) => o.trim()).length >= 2)
      .map((q) => ({
        question: q.question.trim(),
        options: q.options.filter((o) => o.trim()),
        correct_index: parseInt(q.correct_index, 10),
        explanation: (q.explanation || '').trim(),
      }));
    if (clean.length === 0) return toast.error('Add at least one valid question with 2+ options');
    try {
      await api.post(`/academy/admin/course/${courseId}/quiz`, { passing_score: parseInt(passing, 10), questions: clean });
      toast.success(`Quiz saved · ${clean.length} questions`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  return (
    <div className="max-w-3xl mx-auto py-8 px-4 space-y-4" data-testid="academy-quiz-admin">
      <div>
        <h1 className="text-3xl font-extrabold text-white flex items-center gap-2"><Award className="w-7 h-7 text-amber-400" /> Freedom Academy — Course Quizzes</h1>
        <p className="text-slate-400 text-sm mt-1">Students must pass this quiz to unlock the certificate PDF.</p>
      </div>

      <div className="rounded-xl bg-slate-900/70 border border-amber-400/30 p-4 space-y-3">
        <div>
          <label className="text-xs text-amber-300 font-bold uppercase block mb-1">Course</label>
          <select value={courseId} onChange={(e) => setCourseId(e.target.value)} data-testid="quiz-course-select"
            className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm">
            <option value="">— Select Course —</option>
            {courses.map((c) => <option key={c.id} value={c.id}>{(c.title_i18n?.en) || c.title || c.id}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-amber-300 font-bold uppercase block mb-1">Passing Score (%)</label>
          <input type="number" min="1" max="100" value={passing} onChange={(e) => setPassing(e.target.value)}
            data-testid="quiz-passing-input"
            className="w-32 px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm" />
        </div>
      </div>

      {courseId && (
        <div className="space-y-3">
          {questions.map((q, i) => (
            <div key={i} className="rounded-xl bg-slate-900/70 border border-slate-700 p-4 space-y-2" data-testid={`admin-quiz-q-${i}`}>
              <div className="flex items-center justify-between">
                <div className="text-amber-300 text-xs font-bold uppercase">Question {i + 1}</div>
                <button onClick={() => removeQ(i)} className="text-rose-400 hover:text-rose-300"><Trash2 className="w-4 h-4" /></button>
              </div>
              <textarea value={q.question} onChange={(e) => updateQ(i, { question: e.target.value })}
                placeholder="Question text…"
                className="w-full px-3 py-2 rounded bg-slate-800 border border-slate-700 text-white text-sm" rows={2} />
              <div className="space-y-1.5">
                {q.options.map((opt, j) => (
                  <div key={j} className="flex items-center gap-2">
                    <input type="radio" name={`correct-${i}`} checked={q.correct_index === j}
                      onChange={() => updateQ(i, { correct_index: j })} title="Mark as correct answer" />
                    <input type="text" value={opt} placeholder={`Option ${j + 1}`}
                      onChange={(e) => updateOpt(i, j, e.target.value)}
                      className="flex-1 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-white text-sm" />
                  </div>
                ))}
                <button onClick={() => addOpt(i)} className="text-xs text-amber-300 hover:text-amber-200">+ add option</button>
              </div>
              <input type="text" value={q.explanation} onChange={(e) => updateQ(i, { explanation: e.target.value })}
                placeholder="Optional explanation shown after submission"
                className="w-full px-3 py-2 rounded bg-slate-800 border border-slate-700 text-slate-300 text-xs" />
            </div>
          ))}
          <div className="flex gap-2">
            <button onClick={addQ} data-testid="quiz-add-q-btn" className="px-4 py-2 rounded-lg bg-slate-700 text-white text-sm flex items-center gap-2">
              <Plus className="w-4 h-4" /> Add question
            </button>
            <button onClick={save} data-testid="quiz-save-btn"
              className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-bold text-sm flex items-center gap-2">
              <Save className="w-4 h-4" /> Save Quiz
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AcademyQuizAdmin;
